/**
 * Catalog unit prices support mills (one thousandth of a currency unit).
 *
 * This is intentionally separate from invoice and payment totals. A unit can
 * cost $0.012, while the payable line total is still rounded to the currency's
 * minor unit after `unit price × quantity`.
 */
export const CATALOG_UNIT_PRICE_DECIMAL_PLACES = 3;

/**
 * Payable line amounts and invoice totals remain cent amounts. This is a
 * separate boundary from catalog unit prices: multiply the mill-priced unit by
 * quantity first, then round the resulting line amount to this precision.
 */
export const PAYABLE_AMOUNT_DECIMAL_PLACES = 2;

export const CATALOG_UNIT_PRICE_FORMAT_OPTIONS = {
  maximumFractionDigits: CATALOG_UNIT_PRICE_DECIMAL_PLACES,
} as const;

/**
 * Browser-safe half-up rounding for non-negative payable amounts.
 *
 * The exponent shift avoids the common `1.005 * 100` binary-float trap and
 * mirrors the backend's decimal.js ROUND_HALF_UP policy. Negative values are
 * handled symmetrically so the helper remains safe for display calculations.
 */
export function roundPayableAmount(value: number): number {
  if (!Number.isFinite(value)) return value;

  const sign = value < 0 ? -1 : 1;
  const shifted = shiftDecimal(Math.abs(value), PAYABLE_AMOUNT_DECIMAL_PLACES);
  const rounded = Math.round(shifted);
  if (rounded === 0) return 0;
  return sign * shiftDecimal(rounded, -PAYABLE_AMOUNT_DECIMAL_PLACES);
}

function shiftDecimal(value: number, places: number): number {
  const [coefficient, currentExponent = '0'] = String(value).split('e');
  return Number(`${coefficient}e${Number(currentExponent) + places}`);
}

function countSignificantDecimalPlaces(value: string | number): number | null {
  const source = String(value).trim();
  const match = source.match(/^[-+]?(\d+)(?:\.(\d*))?(?:e([-+]?\d+))?$/i)
    ?? source.match(/^[-+]?\.(\d+)(?:e([-+]?\d+))?$/i);
  if (!match) return null;

  const startsWithDecimalPoint = /^[-+]?\./.test(source);
  const integerPart = startsWithDecimalPoint ? '0' : match[1];
  const fractionPart = startsWithDecimalPoint ? match[1] : (match[2] ?? '');
  const exponentText = startsWithDecimalPoint ? match[2] : match[3];
  const exponent = Number(exponentText ?? 0);
  if (!Number.isInteger(exponent)) return null;

  const digitsWithoutInsignificantZeros = `${integerPart}${fractionPart}`.replace(/0+$/, '');
  if (digitsWithoutInsignificantZeros.length === 0) return 0;

  const decimalPoint = integerPart.length + exponent;
  return Math.max(0, digitsWithoutInsignificantZeros.length - decimalPoint);
}

export function hasCatalogUnitPricePrecision(value: string | number): boolean {
  const decimalPlaces = countSignificantDecimalPlaces(value);
  return decimalPlaces !== null && decimalPlaces <= CATALOG_UNIT_PRICE_DECIMAL_PLACES;
}

export function getCatalogUnitPricePrecisionMessage(value: string | number): string {
  return `Price supports at most ${CATALOG_UNIT_PRICE_DECIMAL_PLACES} decimal places; received ${String(value)}.`;
}
